/**
 * TanStack Query key factory — the only place query keys are spelled out.
 * Keys are hierarchical (`['catalog', 'products', filters]`) so `invalidateQueries({ queryKey: queryKeys.catalog.all })`
 * refreshes a whole area. Filters go in as plain serialisable objects.
 */

export type KeyFilters = Readonly<Record<string, unknown>>;

export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    me: () => ['auth', 'me'] as const,
    sessions: () => ['auth', 'sessions'] as const,
    twoFactor: () => ['auth', '2fa'] as const,
    /** Which sign-in providers the API offers (`{ google }`). */
    providers: () => ['auth', 'providers'] as const,
    /** A single-use e-mailed link being spent exactly once (verify e-mail, confirm change, restore …). */
    oneShot: (kind: string, token: string) => ['auth', 'one-shot', kind, token] as const,
  },

  site: {
    all: ['site'] as const,
    settings: () => ['site', 'settings'] as const,
  },

  catalog: {
    all: ['catalog'] as const,
    products: (filters: KeyFilters = {}) => ['catalog', 'products', filters] as const,
    product: (slug: string) => ['catalog', 'product', slug] as const,
    related: (slug: string) => ['catalog', 'product', slug, 'related'] as const,
    facets: (filters: KeyFilters = {}) => ['catalog', 'facets', filters] as const,
    suggest: (q: string) => ['catalog', 'suggest', q] as const,
    categories: () => ['catalog', 'categories'] as const,
    attributes: () => ['catalog', 'attributes'] as const,
    sitemap: () => ['catalog', 'sitemap'] as const,
  },

  /** The public Gallery page's photos (D-332). */
  gallery: {
    all: ['gallery'] as const,
    list: () => ['gallery', 'list'] as const,
  },

  me: {
    all: ['me'] as const,
    profile: () => ['me', 'profile'] as const,
    wishlist: () => ['me', 'wishlist'] as const,
    /** Scoped by user so a previous account's hearts can never show for the next one. */
    wishlistIds: (userId: string) => ['me', 'wishlist', 'ids', userId] as const,
    enquiries: (filters: KeyFilters = {}) => ['me', 'enquiries', filters] as const,
    enquiry: (ref: string) => ['me', 'enquiry', ref] as const,
  },

  admin: {
    all: ['admin'] as const,
    dashboard: () => ['admin', 'dashboard'] as const,
    products: (filters: KeyFilters = {}) => ['admin', 'products', filters] as const,
    product: (id: string) => ['admin', 'product', id] as const,
    categories: () => ['admin', 'categories'] as const,
    attributes: () => ['admin', 'attributes'] as const,
    enquiries: (filters: KeyFilters = {}) => ['admin', 'enquiries', filters] as const,
    enquiry: (id: string) => ['admin', 'enquiry', id] as const,
    media: (filters: KeyFilters = {}) => ['admin', 'media', filters] as const,
    gallery: () => ['admin', 'gallery'] as const,
    content: () => ['admin', 'content'] as const,
    admins: () => ['admin', 'admins'] as const,
  },

  super: {
    all: ['super'] as const,
    users: (filters: KeyFilters = {}) => ['super', 'users', filters] as const,
    user: (id: string) => ['super', 'user', id] as const,
    auditLogs: (filters: KeyFilters = {}) => ['super', 'audit-logs', filters] as const,
    /** The distinct action names, for the audit log's action filter. */
    auditActions: () => ['super', 'audit-actions'] as const,
    /** How many active Super Admins there are (the "last Super Admin" safeguard, shown before it bites). */
    superAdminCount: () => ['super', 'super-admin-count'] as const,
    settings: () => ['super', 'settings'] as const,
    /** The go-live confirmation (D-382). */
    goLive: () => ['super', 'go-live'] as const,
  },
} as const;
